/*
  Builder landing page hero (storage, mega rack, converter, platform, plate tree) (sections/builder-hero.liquid).
  Custom elements, so each instance sets itself up on connect: this keeps working when the
  theme editor re-renders the section, and nothing runs on pages without the section.
*/

/* Horizontal swipe on touch screens; vertical scrolling is left alone. */
function blpSwipe(el, go) {
  if (!el) return;
  let x0 = null;
  let y0 = null;
  el.addEventListener('touchstart', (e) => {
    const t = e.touches[0];
    x0 = t.clientX;
    y0 = t.clientY;
  }, { passive: true });
  el.addEventListener('touchend', (e) => {
    if (x0 === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - x0;
    const dy = t.clientY - y0;
    x0 = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      e.preventDefault();
      go(dx < 0 ? 1 : -1);
    }
  });
}

/* Main photo + thumbnails. Opens <blp-lightbox> (by id in data-lightbox) when zoomed. */
if (!customElements.get('blp-gallery')) {
  customElements.define('blp-gallery', class BlpGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-blp-main]');
      this.thumbs = [...this.querySelectorAll('[data-blp-thumb]')];
      this.counter = this.querySelector('[data-blp-now]');
      this.caption = this.querySelector('[data-blp-caption]');
      this.index = 0;

      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => {
        if (thumb.hasAttribute('data-blp-more')) this.openLightbox(i, thumb);
        else this.show(i);
      }));
      this.querySelectorAll('[data-blp-step]').forEach((btn) => btn.addEventListener('click', () => {
        this.show(this.index + Number(btn.dataset.blpStep));
      }));
      const zoom = this.querySelector('[data-blp-zoom]');
      if (zoom) zoom.addEventListener('click', () => this.openLightbox(this.index, zoom));
      blpSwipe(this.querySelector('.blp-gallery__main'), (d) => this.show(this.index + d));
    }

    get total() {
      return Number(this.dataset.total) || this.thumbs.length;
    }

    show(i) {
      if (!this.thumbs.length) return;
      this.index = (i + this.thumbs.length) % this.thumbs.length;
      const t = this.thumbs[this.index];
      this.main.srcset = t.dataset.srcset;
      this.main.src = t.dataset.src;
      this.main.alt = t.dataset.alt;
      this.thumbs.forEach((b) => b.setAttribute('aria-current', b === t ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = this.index + 1;
      if (this.caption) this.caption.textContent = t.dataset.alt;
    }

    openLightbox(i, opener) {
      const lb = document.getElementById(this.dataset.lightbox);
      if (lb && typeof lb.open === 'function') lb.open(i, opener);
    }
  });
}

/* Full-screen gallery: arrows, swipe, arrow keys, Esc. */
if (!customElements.get('blp-lightbox')) {
  customElements.define('blp-lightbox', class BlpLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.img = this.querySelector('[data-blp-lb-img]');
      this.caption = this.querySelector('[data-blp-lb-caption]');
      this.counter = this.querySelector('[data-blp-lb-now]');
      this.items = [...this.querySelectorAll('[data-blp-lb-go]')];
      this.index = 0;
      this.opener = null;

      this.items.forEach((b, n) => b.addEventListener('click', () => this.show(n)));
      this.querySelectorAll('[data-blp-lb-step]').forEach((b) => b.addEventListener('click', () => {
        this.show(this.index + Number(b.dataset.blpLbStep));
      }));
      this.querySelector('[data-blp-lb-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.show(this.index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.show(this.index + 1); }
      });
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.hasAttribute('data-blp-lb-stage')) this.dialog.close();
      });
      blpSwipe(this.querySelector('[data-blp-lb-stage]'), (d) => this.show(this.index + d));
      this.dialog.addEventListener('close', () => { if (this.opener) this.opener.focus(); });
    }

    open(i, opener) {
      if (!this.dialog) return;
      this.opener = opener || null;
      this.show(i);
      this.dialog.showModal();
    }

    show(n) {
      if (!this.items.length) return;
      this.index = (n + this.items.length) % this.items.length;
      const b = this.items[this.index];
      this.img.src = b.dataset.full;
      this.img.alt = b.dataset.alt;
      this.caption.textContent = b.dataset.alt;
      this.counter.textContent = this.index + 1;
      this.items.forEach((x) => x.setAttribute('aria-current', x === b ? 'true' : 'false'));
      b.scrollIntoView({ block: 'nearest', inline: 'center' });
      const next = this.items[(this.index + 1) % this.items.length];
      if (next) new Image().src = next.dataset.full;
    }
  });
}

/* Video modal: the iframe is only built when opened, and removed on close (stops playback). */
if (!customElements.get('blp-video-modal')) {
  customElements.define('blp-video-modal', class BlpVideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.frame = this.querySelector('[data-blp-video-frame]');
      this.template = this.querySelector('template');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.opener = null;
      this.onOpen = (e) => {
        const btn = e.target.closest(`[aria-controls="${this.id}"]`);
        if (!btn) return;
        e.preventDefault();
        this.open(btn);
      };
      document.addEventListener('click', this.onOpen);
      this.querySelector('[data-blp-video-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => {
        this.frame.innerHTML = '';
        if (this.opener) this.opener.focus();
      });
    }

    disconnectedCallback() {
      if (this.onOpen) document.removeEventListener('click', this.onOpen);
    }

    open(opener) {
      this.opener = opener;
      this.frame.replaceChildren(this.template.content.cloneNode(true));
      this.dialog.showModal();
    }
  });
}

/* Mobile sticky CTA: shown once the hero CTA (data-target id) has scrolled out of view. */
if (!customElements.get('blp-sticky-cta')) {
  customElements.define('blp-sticky-cta', class BlpStickyCta extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-blp-sticky-bar]');
      this.target = document.getElementById(this.dataset.target);
      if (!this.bar || !this.target || !('IntersectionObserver' in window)) return;
      this.update = () => {
        const passed = this.target.getBoundingClientRect().bottom < 0;
        const modalOpen = !!document.querySelector('dialog[open]');
        const inBuilder = this.dataset.hideHash && location.hash === this.dataset.hideHash;
        this.bar.hidden = !passed || modalOpen || inBuilder;
      };
      this.observer = new IntersectionObserver(this.update);
      this.observer.observe(this.target);
      window.addEventListener('hashchange', this.update);
      document.addEventListener('close', this.update, true);
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.update) {
        window.removeEventListener('hashchange', this.update);
        document.removeEventListener('close', this.update, true);
      }
    }
  });
}
